import React, { useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FOCUS_OPTIONS = ["Storytelling", "Sales", "Leadership", "Communication", "Productivity", "Artificial Intelligence", "Another area"];
const EMPTY = { name: "", email: "", mobile: "", company: "", team_size: "", training_focus: "", preferred_dates: "", message: "" };

const Field = ({ id, label, required, full, children }) => (
  <div className={`bp-field${full ? " bp-field--full" : ""}`}>
    <label htmlFor={id} className="bp-field-label">
      {label}{required && <span aria-hidden="true"> *</span>}
    </label>
    {children}
  </div>
);

export const EnquiryForm = ({ production, showFocus = false, prefix, buttonClass = "bp-btn" }) => {
  const [values, setValues] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const id = (k) => `${prefix}-${k}`;
  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.name.trim() || !values.team_size.trim()) return setError("Please complete the required fields.");
    if (!EMAIL_RE.test(values.email.trim())) return setError("Please enter a valid email address.");
    setError("");
    setStatus("loading");
    try {
      const res = await fetch(`${API}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, email: values.email.trim(), production }),
      });
      if (!res.ok) throw new Error("request failed");
      setStatus("done");
    } catch {
      setStatus("idle");
      setError("Something went wrong. Please try again.");
    }
  };

  if (status === "done") {
    return <p className="bp-form-success" role="status" data-testid={`${prefix}-success`}>Thank you — we’ll be in touch shortly.</p>;
  }

  return (
    <form className="bp-form" onSubmit={submit} noValidate data-testid={`${prefix}-form`}>
      <h3 className="bp-form-title">Tell us about your team</h3>
      <div className="bp-form-grid">
        <Field id={id("name")} label="Name" required>
          <input id={id("name")} className="bp-input" value={values.name} onChange={set("name")} autoComplete="name" required data-testid={`${prefix}-name`} />
        </Field>
        <Field id={id("email")} label="Work email" required>
          <input id={id("email")} type="email" className="bp-input" value={values.email} onChange={set("email")} autoComplete="email" required data-testid={`${prefix}-email`} />
        </Field>
        <Field id={id("mobile")} label="Mobile number">
          <input id={id("mobile")} type="tel" className="bp-input" value={values.mobile} onChange={set("mobile")} autoComplete="tel" data-testid={`${prefix}-mobile`} />
        </Field>
        <Field id={id("company")} label="Company">
          <input id={id("company")} className="bp-input" value={values.company} onChange={set("company")} autoComplete="organization" data-testid={`${prefix}-company`} />
        </Field>
        <Field id={id("team-size")} label="Team size" required>
          <input id={id("team-size")} className="bp-input" value={values.team_size} onChange={set("team_size")} inputMode="numeric" required data-testid={`${prefix}-team-size`} />
        </Field>
        {showFocus && (
          <Field id={id("focus")} label="Training focus">
            <select id={id("focus")} className="bp-input" value={values.training_focus} onChange={set("training_focus")} data-testid={`${prefix}-focus`}>
              <option value=""></option>
              {FOCUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
        )}
        <Field id={id("dates")} label="Preferred dates" full={!showFocus}>
          <input id={id("dates")} className="bp-input" value={values.preferred_dates} onChange={set("preferred_dates")} data-testid={`${prefix}-dates`} />
        </Field>
        <Field id={id("message")} label="Anything else (optional)" full>
          <textarea id={id("message")} className="bp-input bp-textarea" rows="4" value={values.message} onChange={set("message")} data-testid={`${prefix}-message`} />
        </Field>
      </div>
      {error && <p className="bp-form-error" role="alert" data-testid={`${prefix}-error`}>{error}</p>}
      <button type="submit" className={buttonClass} disabled={status === "loading"} data-testid={`${prefix}-submit`}>SEND ENQUIRY</button>
    </form>
  );
};
